import "./CmsSubmissionModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import { initiateDownload } from "components/common/DownloadLink";
import LoadingIcon from "components/Loading/LoadingIcon";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useState } from "react";
import { api } from "services/api";
import {
  getCmsPackageItemUrl,
  getCmsPackageZipUrl,
  isCmsPackageActive,
  isCmsPackageStale,
  useCreateCmsSubmissionPackageMutation,
  useGetCmsSubmissionPackagesQuery,
  useGetCmsSubmissionPlanQuery,
} from "services/cms-submission-api";
import { DATE_TO_STRING } from "utils/date-utils";
import { useDispatch, useSelector } from "utils/redux";

import type {
  CmsNonPdfDocument,
  CmsSubmissionPackage,
  CmsSubmissionWarnings,
  CmsUndatedRecord,
} from "services/cms-submission-api";

const POLLING_INTERVAL_MS = 5000;
const STATUS_LABELS: Record<CmsSubmissionPackage["status"], string> = {
  pending: "Queued",
  processing: "Generating",
  completed: "Ready",
  failed: "Failed",
};

const formatTimestamp = (value: string | null) =>
  value ? DATE_TO_STRING(new Date(value), "MM/dd/yyyy h:mm a") : "";

const getApiErrorMessage = (error: unknown, fallback: string) =>
  error && typeof error === "object" && "data" in error
    ? (error.data as { error?: string })?.error || fallback
    : fallback;

export const hasWarnings = (warnings: Partial<CmsSubmissionWarnings>) =>
  Boolean(
    warnings.missing_sections?.length ||
    warnings.skipped_documents?.length ||
    warnings.warnings?.length,
  );

export const CmsWarningList = ({
  warnings,
}: {
  warnings: Partial<CmsSubmissionWarnings>;
}) => {
  if (!hasWarnings(warnings)) {
    return null;
  }
  return (
    <div className="cms-submission__warnings" role="alert">
      {Boolean(warnings.missing_sections?.length) && (
        <p>
          <strong>Missing required sections:</strong>{" "}
          {warnings.missing_sections
            ?.map((missing) => missing.label)
            .join(", ")}
        </p>
      )}
      {Boolean(warnings.skipped_documents?.length) && (
        <>
          <strong>
            Documents marked for CMS Submission but will not be included in the
            archive:
          </strong>
          <ul>
            {warnings.skipped_documents?.map((skipped) => (
              <li key={`${skipped.document_id}-${skipped.filename}`}>
                {skipped.filename}: {skipped.reason}
              </li>
            ))}
          </ul>
        </>
      )}
      {warnings.warnings?.map((warning) => (
        <p key={warning}>{warning}</p>
      ))}
    </div>
  );
};

const pageRange = (record: CmsUndatedRecord) =>
  record.start_page === record.end_page
    ? `Page ${record.start_page}`
    : `Pages ${record.start_page}-${record.end_page}`;

const UndatedRecordList = ({ records }: { records: CmsUndatedRecord[] }) => (
  <div
    className="cms-submission__warnings cms-submission__warnings--blocking"
    role="alert"
  >
    <strong>
      These documents tagged for CMS submission are undated. Add a date to each
      one in the Metadoc before generating:
    </strong>
    <ul>
      {records.map((record) => (
        <li key={record.id}>
          {[pageRange(record), record.provider, record.document_type]
            .filter(Boolean)
            .join(" · ")}
        </li>
      ))}
    </ul>
  </div>
);

const NonPdfDocumentList = ({
  documents,
}: {
  documents: CmsNonPdfDocument[];
}) => (
  <div
    className="cms-submission__warnings cms-submission__warnings--blocking"
    role="alert"
  >
    <strong>
      These documents checked for CMS are not PDFs. Upload a PDF version or
      uncheck them before generating:
    </strong>
    <ul>
      {documents.map((document) => (
        <li key={document.document_id}>{document.filename}</li>
      ))}
    </ul>
  </div>
);

const MissingSectionList = ({
  sections,
}: {
  sections: CmsSubmissionWarnings["missing_sections"];
}) => (
  <div
    className="cms-submission__warnings cms-submission__warnings--blocking"
    role="alert"
  >
    <strong>
      These required sections are missing. Add a document checked for CMS to
      each one before generating:
    </strong>
    <ul>
      {sections.map((missing) => (
        <li key={missing.section}>{missing.label}</li>
      ))}
    </ul>
  </div>
);

type PackageHistoryProps = {
  packages: CmsSubmissionPackage[];
  onDownload: (url: string, filename: string) => void;
};

const PackageHistory = ({ packages, onDownload }: PackageHistoryProps) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (packages.length === 0) {
    return <p>No packages have been generated yet.</p>;
  }

  return (
    <ul className="cms-submission__history">
      {packages.map((pkg) => {
        const isExpanded = expandedId === pkg.id;
        const isReady = pkg.status === "completed";
        return (
          <li key={pkg.id} className="cms-submission__package">
            <div className="cms-submission__package-row">
              <div>
                <strong>Version {pkg.package_version}</strong>
                <span
                  className={`cms-submission__status cms-submission__status--${pkg.status}`}
                >
                  {isCmsPackageStale(pkg)
                    ? "Did not finish"
                    : STATUS_LABELS[pkg.status]}
                </span>
                <div className="cms-submission__meta">
                  {formatTimestamp(pkg.created)} by {pkg.created_by_name}
                </div>
              </div>
              {isReady && (
                <div className="cms-submission__package-actions">
                  <Button
                    onClick={() => setExpandedId(isExpanded ? null : pkg.id)}
                  >
                    {isExpanded ? "Hide Files" : `Files (${pkg.items.length})`}
                  </Button>
                  <Button
                    onClick={() =>
                      onDownload(
                        getCmsPackageZipUrl(pkg.id),
                        `CMSSubmissionV${pkg.package_version}.zip`,
                      )
                    }
                  >
                    <span className="material-icons">download</span>
                    Zip
                  </Button>
                </div>
              )}
            </div>
            {pkg.status === "failed" && (
              <>
                {pkg.error_message && (
                  <p className="cms-submission__error">{pkg.error_message}</p>
                )}
                <CmsWarningList warnings={pkg.warnings} />
              </>
            )}
            {isReady && isExpanded && (
              <>
                <CmsWarningList warnings={pkg.warnings} />
                <ul className="cms-submission__files">
                  {pkg.items.map((item) => (
                    <li key={item.id}>
                      <span className="cms-submission__filename">
                        {item.filename}
                      </span>
                      <span className="cms-submission__meta">
                        {item.page_count} page{item.page_count === 1 ? "" : "s"}
                      </span>
                      <button
                        type="button"
                        className="material-icons cms-submission__file-download"
                        title={`Download ${item.filename}`}
                        aria-label={`Download ${item.filename}`}
                        onClick={() =>
                          onDownload(
                            getCmsPackageItemUrl(pkg.id, item.id),
                            item.filename,
                          )
                        }
                      >
                        download
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
};

type CmsSubmissionModalProps = {
  fileId: number;
  onClose: () => void;
};

const CmsSubmissionModal = ({ fileId, onClose }: CmsSubmissionModalProps) => {
  const accessToken = useSelector((state) => state.main.accessToken);
  const dispatch = useDispatch();
  const [pollingInterval, setPollingInterval] = useState(0);
  const { data: plan, isFetching: isPlanLoading } =
    useGetCmsSubmissionPlanQuery(
      { fileId },
      { refetchOnMountOrArgChange: true },
    );
  const { data: packagesPage } = useGetCmsSubmissionPackagesQuery(
    { fileId },
    { pollingInterval, refetchOnMountOrArgChange: true },
  );
  const [createPackage, { isLoading: isCreating }] =
    useCreateCmsSubmissionPackageMutation();

  const packages = packagesPage?.results ?? [];
  const isBuilding = packages.some(
    (pkg) => isCmsPackageActive(pkg) && !isCmsPackageStale(pkg),
  );
  useEffect(() => {
    setPollingInterval(isBuilding ? POLLING_INTERVAL_MS : 0);
  }, [isBuilding]);

  const hasUndatedRecords = Boolean(plan?.undated_records.length);
  const hasNonPdfDocuments = Boolean(plan?.non_pdf_documents.length);
  const hasMissingSections = Boolean(plan?.missing_sections.length);
  const canGenerate =
    Boolean(plan?.items.length) &&
    !hasUndatedRecords &&
    !hasNonPdfDocuments &&
    !hasMissingSections &&
    !isBuilding &&
    !isCreating &&
    !isPlanLoading;

  const handleGenerate = async () => {
    try {
      await createPackage({ fileId }).unwrap();
      KaboodleToaster.success("CMS submission package generation started");
    } catch (error) {
      KaboodleToaster.error(
        getApiErrorMessage(error, "Failed to start package generation"),
      );
    }
  };

  const handleDownload = (url: string, filename: string) =>
    initiateDownload(url, accessToken, filename);

  const handleClose = () => {
    dispatch(api.util.invalidateTags(["CmsSubmissionPackages"]));
    onClose();
  };

  return (
    <KaboodleModal
      isOpen
      onClose={handleClose}
      size="large"
      className="cms-submission"
      footer={
        <div className="flex jc-c">
          <Button onClick={() => void handleGenerate()} disabled={!canGenerate}>
            {isCreating || isBuilding ? "Generating..." : "Generate Package"}
          </Button>
          <Button onClick={handleClose}>Close</Button>
        </div>
      }
    >
      <Card heading="CMS Submission Package">
        {isPlanLoading && !plan ? (
          <LoadingIcon message="Checking documents" />
        ) : (
          plan && (
            <>
              {hasUndatedRecords && (
                <UndatedRecordList records={plan.undated_records} />
              )}
              {hasNonPdfDocuments && (
                <NonPdfDocumentList documents={plan.non_pdf_documents} />
              )}
              {hasMissingSections && (
                <MissingSectionList sections={plan.missing_sections} />
              )}
              {/* Missing sections already have their own list above. */}
              <CmsWarningList warnings={{ ...plan, missing_sections: [] }} />
              {plan.items.length === 0 ? (
                <p>Nothing can be packaged yet.</p>
              ) : (
                <ol className="cms-submission__files cms-submission__plan">
                  {plan.items.map((item) => (
                    <li key={item.filename}>
                      <span className="cms-submission__position">
                        {item.position}
                      </span>
                      <span className="cms-submission__filename">
                        {item.filename}
                      </span>
                      <span className="cms-submission__meta">
                        {item.source_filename ??
                          `${item.record_group_count} tagged record${item.record_group_count === 1 ? "" : "s"}`}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )
        )}
      </Card>
      <Card heading="Generated Packages">
        <PackageHistory packages={packages} onDownload={handleDownload} />
      </Card>
    </KaboodleModal>
  );
};

export default CmsSubmissionModal;
